/**
 * PostDetail Component
 * 글 상세 보기 + AI Q&A
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth, USER_TIERS } from '../../contexts/AuthContext';
import { useCommunity, POST_TYPES } from '../../contexts/CommunityContext';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import rehypeRaw from 'rehype-raw';
import { useNotification, NOTIFICATION_TYPES } from '../../contexts/NotificationContext';
import './ai-answer.css';
import { formatDate } from './utils';

// =============================================================================
// 용어 툴팁 — stock-terms.json 기반 자동 치환 (프론트엔드 렌더링 시 적용)
// =============================================================================

let stockTermsCache = null;

async function loadStockTerms() {
  if (stockTermsCache) return stockTermsCache;
  try {
    const res = await fetch('/data/stock-terms.json');
    if (res.ok) {
      stockTermsCache = await res.json();
      return stockTermsCache;
    }
  } catch {}
  return {};
}

/**
 * 본문에서 용어 사전 단어를 <span class="tip"> 태그로 감싼다.
 * - 마크다운 테이블 행 스킵
 * - 기존 HTML 태그 내부 스킵
 * - 긴 용어 우선 매칭 (한 패스 치환, 이중 래핑 방지)
 */
function applyTermTooltips(text, terms) {
  if (!terms || Object.keys(terms).length === 0) return text;

  const sorted = Object.entries(terms).sort((a, b) => b[0].length - a[0].length);
  const pattern = sorted.map(([term]) =>
    term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  ).join('|');
  const re = new RegExp(pattern, 'g');
  const termMap = Object.fromEntries(sorted);

  return text.split('\n').map(line => {
    const trimmed = line.trim();
    // 테이블 행은 스킵 (ReactMarkdown components.td에서 처리)
    if (trimmed.startsWith('|') && trimmed.endsWith('|')) return line;
    if (/^[\s|:\-─━]+$/.test(trimmed)) return line;

    // HTML 태그 기준으로 분리 → 태그 밖 + tip 태그 밖 텍스트만 치환
    const parts = line.split(/(<[^>]+>)/);
    let insideTip = false;
    return parts.map(part => {
      if (part.startsWith('<')) {
        if (part.startsWith('<span') && part.includes('class="tip"')) insideTip = true;
        else if (part === '</span>' && insideTip) insideTip = false;
        return part;
      }
      if (insideTip) return part; // 이미 툴팁 안 — 스킵
      return part.replace(re, (match) => {
        const desc = termMap[match];
        if (!desc) return match;
        const safeDesc = desc.replace(/"/g, '&quot;');
        return `<span class="tip" data-tip="${safeDesc}">${match}</span>`;
      });
    }).join('');
  }).join('\n');
}

// =============================================================================

const PostDetail = ({ post, onBack }) => {
  const { t } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const { deletePost, addComment, deleteComment, getPostById } = useCommunity();
  const { addNotification } = useNotification();

  const [currentPost, setCurrentPost] = useState(post);
  const [newComment, setNewComment] = useState('');
  const [message, setMessage] = useState('');

  // AI Q&A state
  const [aiQuestion, setAiQuestion] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');
  const [aiCredits, setAiCredits] = useState({ remaining: 0, limit: 0, tier: 'general' });

  // Q&A 토글 state
  const [expandedQA, setExpandedQA] = useState({});

  // 용어 툴팁 사전
  const [stockTerms, setStockTerms] = useState(null);

  // 입력 모드: 'comment' | 'ai'
  const [inputMode, setInputMode] = useState('comment');
  // AI 질문 모드: 'normal' | 'deep'
  const [aiMode, setAiMode] = useState('normal');

  const isAdmin = user?.tier === USER_TIERS.ADMIN;
  const canPost = isAuthenticated && user?.tier !== USER_TIERS.GUEST;
  const canAI = isAuthenticated && ['subscriber', 'family', 'admin'].includes(user?.tier);

  // 댓글 그룹화: 일반 댓글 vs AI Q&A
  const { regularComments, qaGroups } = useMemo(() => {
    const regular = [];
    const questions = [];
    const answerMap = {};

    (currentPost.comments || []).forEach(c => {
      if (c.comment_type === 'ai_question') {
        questions.push(c);
      } else if (c.comment_type?.startsWith('ai_answer')) {
        const pid = c.parent_id;
        if (!answerMap[pid]) answerMap[pid] = [];
        answerMap[pid].push(c);
      } else {
        regular.push(c);
      }
    });

    const groups = questions.map(q => ({
      question: q,
      answers: answerMap[q.id] || [],
    }));

    return { regularComments: regular, qaGroups: groups };
  }, [currentPost.comments]);

  const toggleQA = (qId) => {
    setExpandedQA(prev => ({ ...prev, [qId]: !prev[qId] }));
  };

  // formatDate: community/utils.js에서 import

  // AI 크레딧 조회
  const fetchAICredits = async () => {
    try {
      const token = document.cookie.split(';').find(c => c.trim().startsWith('ilouli_token='))?.split('=')[1];
      if (!token) return;
      const res = await fetch('/api/community/ai-question/remaining', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setAiCredits(data);
      }
    } catch {}
  };

  useEffect(() => {
    if (canAI) fetchAICredits();
  }, [isAuthenticated, user?.tier]);

  // stock/tip 카테고리: 용어 사전 로드
  useEffect(() => {
    if (['stock', 'tip'].includes(currentPost.category)) {
      loadStockTerms().then(setStockTerms);
    }
  }, [currentPost.category]);

  // 용어 툴팁이 적용된 콘텐츠
  const processedContent = useMemo(() => {
    if (!['stock', 'tip'].includes(currentPost.category) || !stockTerms) {
      return currentPost.content;
    }
    return applyTermTooltips(currentPost.content, stockTerms);
  }, [currentPost.content, currentPost.category, stockTerms]);

  // AI 질문 핸들러
  const handleAIQuestion = async (mode = 'normal') => {
    if (!aiQuestion.trim()) return;
    setAiLoading(true);
    setAiError('');

    try {
      const token = document.cookie.split(';').find(c => c.trim().startsWith('ilouli_token='))?.split('=')[1];
      const res = await fetch(`/api/community/posts/${currentPost.id}/ai-question`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ question: aiQuestion.trim(), mode }),
      });

      const data = await res.json();
      if (!data.success) {
        setAiError(data.error);
        return;
      }

      const newQuestionId = data.questionComment.id;

      setCurrentPost(prev => ({
        ...prev,
        comments: [
          ...(prev.comments || []),
          {
            id: newQuestionId,
            author: { id: user.id, name: user.displayName || user.nickname },
            content: data.questionComment.content,
            comment_type: 'ai_question',
            createdAt: new Date().toISOString(),
          },
          {
            id: data.answerComment.id,
            author: { id: 0, name: 'Tina AI' },
            content: data.answerComment.content,
            comment_type: data.answerComment.comment_type,
            model: data.answerComment.model,
            createdAt: new Date().toISOString(),
            parent_id: newQuestionId,
          },
        ],
      }));

      setExpandedQA(prev => ({ ...prev, [newQuestionId]: true }));
      setAiQuestion('');
      setAiCredits(prev => ({ ...prev, remaining: data.creditsRemaining }));
    } catch (err) {
      setAiError('답변 생성에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setAiLoading(false);
    }
  };

  // 심층 재분석 핸들러
  const handleDeepen = async (questionId, answerId) => {
    setAiLoading(true);
    setAiError('');

    try {
      const token = document.cookie.split(';').find(c => c.trim().startsWith('ilouli_token='))?.split('=')[1];
      const res = await fetch(`/api/community/posts/${currentPost.id}/ai-question/deepen`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ originalQuestionId: questionId, originalAnswerId: answerId }),
      });

      const data = await res.json();
      if (!data.success) {
        setAiError(data.error);
        return;
      }

      setCurrentPost(prev => ({
        ...prev,
        comments: [
          ...(prev.comments || []),
          {
            id: data.answerComment.id,
            author: { id: 0, name: 'Tina AI' },
            content: data.answerComment.content,
            comment_type: 'ai_answer_deep',
            model: 'sonnet',
            createdAt: new Date().toISOString(),
            parent_id: questionId,
          },
        ],
      }));

      setAiCredits(prev => ({ ...prev, remaining: data.creditsRemaining }));
    } catch (err) {
      setAiError('심층 분석 생성에 실패했습니다.');
    } finally {
      setAiLoading(false);
    }
  };

  const handleDeletePost = () => {
    if (window.confirm('정말 삭제하시겠습니까?')) {
      try {
        deletePost(currentPost.id, user.id, isAdmin);
        onBack();
      } catch (err) {
        setMessage('삭제에 실패했습니다.');
      }
    }
  };

  const handleAddComment = (e) => {
    e.preventDefault();
    if (!newComment.trim() || !currentPost) return;

    addComment(currentPost.id, newComment, user);
    setNewComment('');

    const updated = getPostById(currentPost.id);
    setCurrentPost(updated);

    if (currentPost.author.id !== user.id) {
      addNotification(
        currentPost.author.id,
        NOTIFICATION_TYPES.COMMENT,
        `${user.displayName || user.nickname}님이 댓글을 남겼습니다`,
        `"${currentPost.title}"`,
        `/`
      );
    }
  };

  const handleDeleteComment = (commentId) => {
    if (window.confirm('댓글을 삭제하시겠습니까?')) {
      try {
        deleteComment(currentPost.id, commentId, user.id, isAdmin);
        const updated = getPostById(currentPost.id);
        setCurrentPost(updated);
      } catch (err) {
        setMessage('댓글 삭제에 실패했습니다.');
      }
    }
  };

  const totalComments = regularComments.length + qaGroups.length;

  return (
    <div className="section-container">
      <button className="back-btn" onClick={onBack}>
        ← 목록으로
      </button>

      {message && <div className="message error">{message}</div>}

      <article className="post-detail">
        <header className="post-detail-header">
          <div className="post-badges">
            {currentPost.type === POST_TYPES.ANNOUNCEMENT && (
              <span className="post-badge announcement">공지</span>
            )}
            {currentPost.category && (
              <span className={`post-badge category-${currentPost.category}`}>
                {t(`community.categories.${currentPost.category}`, currentPost.category)}
              </span>
            )}
          </div>
          <h1>{currentPost.title}</h1>
          <div className="post-meta">
            <span className="author">{currentPost.author.name}</span>
            <span className="date">{formatDate(currentPost.createdAt)}</span>
          </div>
        </header>

        <div className={`post-content${["stock", "tip"].includes(currentPost.category) ? " stock-analysis" : ""}`}>
          <ReactMarkdown
                remarkPlugins={[remarkGfm, remarkBreaks]}
                rehypePlugins={[rehypeRaw]}
                components={{
                  iframe: ({ node, ...props }) => {
                    const src = props.src || '';
                    if (src.includes('stock.ilouli.com') || src.includes('ilouli.com')) {
                      return <iframe {...props} style={{ width: '100%', border: '1px solid #e0e0e0', borderRadius: 8, margin: '16px 0' }} />;
                    }
                    return null;
                  },
                  td: ({ node, children, ...props }) => {
                    if (!stockTerms || !children) return <td {...props}>{children}</td>;
                    const text = typeof children === 'string' ? children
                      : Array.isArray(children) ? children.map(c => typeof c === 'string' ? c : '').join('') : '';
                    if (!text) return <td {...props}>{children}</td>;
                    const sorted = Object.entries(stockTerms).sort((a, b) => b[0].length - a[0].length);
                    const pat = sorted.map(([t]) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
                    const re = new RegExp(`(${pat})`);
                    const parts = text.split(re);
                    if (parts.length <= 1) return <td {...props}>{children}</td>;
                    return <td {...props}>{parts.map((p, i) => stockTerms[p]
                      ? <span key={i} className="tip" data-tip={stockTerms[p]}>{p}</span>
                      : p
                    )}</td>;
                  },
                  th: ({ node, children, ...props }) => {
                    if (!stockTerms || !children) return <th {...props}>{children}</th>;
                    const text = typeof children === 'string' ? children
                      : Array.isArray(children) ? children.map(c => typeof c === 'string' ? c : '').join('') : '';
                    if (!text) return <th {...props}>{children}</th>;
                    const sorted = Object.entries(stockTerms).sort((a, b) => b[0].length - a[0].length);
                    const pat = sorted.map(([t]) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
                    const re = new RegExp(`(${pat})`);
                    const parts = text.split(re);
                    if (parts.length <= 1) return <th {...props}>{children}</th>;
                    return <th {...props}>{parts.map((p, i) => stockTerms[p]
                      ? <span key={i} className="tip" data-tip={stockTerms[p]}>{p}</span>
                      : p
                    )}</th>;
                  },
                }}
              >{processedContent}</ReactMarkdown>
        </div>

        {(currentPost.author.id === user?.id || isAdmin) && (
          <div className="post-actions">
            <button onClick={handleDeletePost} className="delete-btn">
              삭제
            </button>
          </div>
        )}
      </article>

      {/* 통합 댓글/AI 섹션 */}
      <section className="comments-section">
        <h2>댓글 ({totalComments})</h2>

        {/* 입력 영역 — 탭 전환 */}
        {canPost && (
          <div className="comment-input-area">
            <div className="comment-input-tabs">
              <button
                className={`comment-tab ${inputMode === 'comment' ? 'active' : ''}`}
                onClick={() => setInputMode('comment')}
              >
                ✏️ 의견달기
              </button>
              {canAI ? (
                <button
                  className={`comment-tab tab-ai ${inputMode === 'ai' ? 'active' : ''}`}
                  onClick={() => setInputMode('ai')}
                >
                  🤖 AI에게 질문하기
                </button>
              ) : (
                <button className="comment-tab tab-ai disabled" disabled title="구독자 이상만 가능">
                  🤖 AI에게 질문하기
                </button>
              )}
            </div>

            {inputMode === 'comment' ? (
              /* 일반 댓글 입력 */
              <form onSubmit={handleAddComment} className="comment-form">
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="댓글을 입력하세요"
                  rows={3}
                />
                <button type="submit" disabled={!newComment.trim()}>
                  댓글 작성
                </button>
              </form>
            ) : (
              /* AI 질문 입력 */
              <div className="ai-question-form">
                <textarea
                  value={aiQuestion}
                  onChange={(e) => setAiQuestion(e.target.value)}
                  placeholder="이 글에 대해 궁금한 점을 입력하세요..."
                  rows={3}
                  disabled={aiLoading}
                />
                <div className="ai-question-actions">
                  <div className="ai-mode-select">
                    <button
                      className={`ai-mode-btn ${aiMode === 'normal' ? 'active' : ''}`}
                      onClick={() => setAiMode('normal')}
                    >
                      💬 일반 (1cr)
                    </button>
                    <button
                      className={`ai-mode-btn ai-mode-deep ${aiMode === 'deep' ? 'active' : ''}`}
                      onClick={() => setAiMode('deep')}
                    >
                      🔍 심층 (3cr)
                    </button>
                  </div>
                  <span className="ai-credits-remaining">
                    남은 크레딧: {aiCredits.remaining === 'unlimited' ? '무제한' : `${aiCredits.remaining}/${aiCredits.limit}`}
                  </span>
                  <button
                    onClick={() => handleAIQuestion(aiMode)}
                    disabled={!aiQuestion.trim() || aiLoading}
                    className="ai-submit-btn"
                  >
                    질문하기
                  </button>
                </div>
                {aiLoading && <div className="ai-loading">AI가 답변을 작성 중입니다...</div>}
                {aiError && <div className="ai-error">{aiError}</div>}
              </div>
            )}
          </div>
        )}

        {/* 일반 댓글 목록 */}
        <div className="comments-list">
          {totalComments === 0 && (
            <p className="no-comments">아직 댓글이 없습니다.</p>
          )}
          {regularComments.map((comment) => (
            <div key={comment.id} className="comment-item">
              <div className="comment-header">
                <span className="comment-author">{comment.author.name}</span>
                <span className="comment-date">{formatDate(comment.createdAt)}</span>
              </div>
              <p className="comment-content">{comment.content}</p>
              {(comment.author?.id === user?.id || isAdmin) && (
                <button
                  onClick={() => handleDeleteComment(comment.id)}
                  className="comment-delete-btn"
                >
                  삭제
                </button>
              )}
            </div>
          ))}
        </div>

        {/* AI Q&A 그룹 */}
        {qaGroups.length > 0 && (
          <div className="ai-qa-list">
            <h4 className="ai-qa-list-title">AI Q&A ({qaGroups.length})</h4>
            {qaGroups.map(({ question, answers }) => (
              <div key={question.id} className="ai-qa-group">
                <div className="ai-qa-question" onClick={() => toggleQA(question.id)}>
                  <div className="ai-qa-question-header">
                    <span className="ai-qa-asker">{question.author.name}</span>
                    <span className="comment-badge ai-question-badge">AI 질문</span>
                    <span className="comment-date">{formatDate(question.createdAt)}</span>
                  </div>
                  <p className="ai-qa-question-text">{question.content}</p>
                  {answers.length > 0 && (
                    <button className="ai-qa-toggle" onClick={(e) => { e.stopPropagation(); toggleQA(question.id); }}>
                      {expandedQA[question.id] ? '답변 접기 ▲' : `답변 보기 ▼ (${answers.length})`}
                    </button>
                  )}
                </div>

                {expandedQA[question.id] && answers.length > 0 && (
                  <div className="ai-qa-answers">
                    {answers.map(answer => (
                      <div key={answer.id} className={`ai-qa-answer ${
                        answer.comment_type === 'ai_answer_deep' ? 'ai-qa-answer-deep' : ''
                      }`}>
                        <div className="comment-header">
                          <span className="comment-author">🤖 Tina AI</span>
                          {answer.comment_type === 'ai_answer' && (
                            <span className="comment-badge ai-answer-badge">AI 답변 · Haiku</span>
                          )}
                          {answer.comment_type === 'ai_answer_deep' && (
                            <span className="comment-badge ai-deep-badge">AI 심층 분석 · Sonnet</span>
                          )}
                          <span className="comment-date">{formatDate(answer.createdAt)}</span>
                        </div>
                        <div className="ai-answer-content">
                          <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]} rehypePlugins={[rehypeRaw]}>
                            {answer.content}
                          </ReactMarkdown>
                        </div>
                        {answer.comment_type === 'ai_answer' && canAI && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeepen(answer.parent_id, answer.id); }}
                            disabled={aiLoading}
                            className="ai-deepen-btn"
                          >
                            🔍 더 깊이 분석해줘 (3cr)
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default PostDetail;
